import React, { useState } from 'react';
import AuthLayout from 'layouts/AuthLayout';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import 'styles/CardStyles.css';

import { makeRequest } from 'services/request.service';
import { showAlert } from 'services/alerts.service';

const SignUpCard = () => {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [repeatEmail, setRepeatEmail] = useState('');
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [error, setError] = useState('');

  const [accessCode, setAccessCode] = useState('');
  const [unlocked, setUnlocked] = useState(false);
  const secret = 'letmein';
  const navigate = useNavigate();

  const validateForm = () => {
    if (!email) {
      setError(t('validation.emailRequired'));
      return false;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError(t('validation.emailInvalid'));
      return false;
    }
    if (email !== repeatEmail) {
      setError(t('validation.emailMismatch'));
      return false;
    }
    if (!password) {
      setError(t('validation.passwordRequired'));
      return false;
    }
    if (password.length < 6) {
      setError(t('validation.passwordMinLength'));
      return false;
    }
    if (password !== repeatPassword) {
      setError(t('validation.passwordMismatch'));
      return false;
    }
    setError('');
    return true;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!validateForm()) {
      return; // Stop if validation fails
    }

    try {
      const result = await makeRequest({
        method: 'post',
        data: {
          firstName,
          lastName,
          email,
          password
        },
        url: '/signup',
        alertResult: false // Handle success message manually
      });

      if (result.status === 201) {
        showAlert({
          text: t('auth.signupSuccess'),
          icon: 'success'
        });
        navigate('/login');
      }
    } catch (error) {
      console.error('Signup error:', error.response?.data || error.message);
      setError(error.response?.data?.message || t('auth.signupFailed'));
      showAlert({
        text: error.response?.data?.message || t('auth.signupError'),
        icon: 'error'
      });
    }
  };

  if (!unlocked) {
    return (
      <AuthLayout>
        <h2 className="font-command-display text-2xl font-bold text-greenPrimary mb-1">
          {t('auth.enterInviteCode')}
        </h2>
        <p className="auth-subtitle font-command-label text-xs uppercase tracking-widest mb-8">
          {t('auth.inviteCode')}
        </p>
        <input
          type="password"
          value={accessCode}
          onChange={e => setAccessCode(e.target.value)}
          placeholder={t('auth.inviteCode')}
          className="auth-input w-full px-3 py-2.5 mb-5 border rounded-md text-sm transition"
        />
        <button
          onClick={() => {
            if (accessCode === secret) {
              setUnlocked(true);
            } else {
              showAlert({
                text: t('auth.invalidCode'),
                icon: 'error'
              });
            }
          }}
          className="workspace-primary-action w-full font-command-label text-xs uppercase tracking-widest py-2.5 rounded-md transition"
        >
          {t('common.unlock')}
        </button>
        <div className="auth-muted mt-6 text-center text-sm">
          {t('auth.alreadyHaveAccount')}{' '}
          <a
            href="#login"
            onClick={e => {
              e.preventDefault();
              navigate('/login');
            }}
            className="auth-link font-semibold hover:underline"
          >
            {t('auth.logIn')}
          </a>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <h2 className="font-command-display text-2xl font-bold text-greenPrimary mb-1">
        {t('auth.createAccount')}
      </h2>
      <p className="auth-subtitle font-command-label text-xs uppercase tracking-widest mb-8">
        {t('auth.signUp')}
      </p>

      <form onSubmit={handleSubmit} autoComplete="off" className="w-full">
        {error && <p className="auth-error text-sm mb-4 text-center">{error}</p>}

        <div className="mb-4">
          <label className="auth-label block font-command-label text-xs uppercase tracking-widest mb-1.5">
            {t('form.firstName')}
          </label>
          <input
            type="text"
            name="firstName"
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            className="auth-input w-full px-3 py-2.5 border rounded-md text-sm transition"
          />
        </div>

        <div className="mb-4">
          <label className="auth-label block font-command-label text-xs uppercase tracking-widest mb-1.5">
            {t('form.lastName')}
          </label>
          <input
            type="text"
            name="lastName"
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            className="auth-input w-full px-3 py-2.5 border rounded-md text-sm transition"
          />
        </div>

        <div className="mb-4">
          <label className="auth-label block font-command-label text-xs uppercase tracking-widest mb-1.5">
            {t('form.emailAddress')}
          </label>
          <input
            type="email"
            name="email"
            placeholder={t('form.emailPlaceholder')}
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="auth-input w-full px-3 py-2.5 border rounded-md text-sm transition"
          />
        </div>

        <div className="mb-4">
          <label className="auth-label block font-command-label text-xs uppercase tracking-widest mb-1.5">
            {t('form.repeatEmailAddress')}
          </label>
          <input
            type="email"
            name="repeatEmail"
            placeholder={t('form.emailPlaceholder')}
            value={repeatEmail}
            onChange={e => setRepeatEmail(e.target.value)}
            className="auth-input w-full px-3 py-2.5 border rounded-md text-sm transition"
          />
        </div>

        <div className="mb-4">
          <label className="auth-label block font-command-label text-xs uppercase tracking-widest mb-1.5">
            {t('form.password')}
          </label>
          <input
            type="password"
            name="password"
            placeholder={t('form.passwordPlaceholder')}
            value={password}
            onChange={e => setPassword(e.target.value)}
            className="auth-input w-full px-3 py-2.5 border rounded-md text-sm transition"
          />
        </div>

        <div className="mb-7">
          <label className="auth-label block font-command-label text-xs uppercase tracking-widest mb-1.5">
            {t('form.repeatPassword')}
          </label>
          <input
            type="password"
            name="repeatPassword"
            placeholder={t('form.passwordPlaceholder')}
            value={repeatPassword}
            onChange={e => setRepeatPassword(e.target.value)}
            className="auth-input w-full px-3 py-2.5 border rounded-md text-sm transition"
          />
        </div>

        <button
          type="submit"
          className="workspace-primary-action w-full font-command-label text-xs uppercase tracking-widest py-2.5 rounded-md transition"
        >
          {t('auth.signUp')}
        </button>
      </form>

      <div className="auth-muted mt-6 text-center text-sm">
        {t('auth.alreadyHaveAccount')}{' '}
        <a
          href="#login"
          onClick={e => {
            e.preventDefault();
            navigate('/login');
          }}
          className="auth-link font-semibold hover:underline"
        >
          {t('auth.logIn')}
        </a>
      </div>
    </AuthLayout>
  );
};

export default SignUpCard;
